// @ts-check
/* eslint-env browser */

import { reactiveStore } from '../../reactive-store.js'

/**
 * Web component for handling "Add tree" / "Add zone" form submissions without page reload.
 *
 * @requires FeatureAutosave — persists the client-UUID feature via store subscription
 *
 * Usage:
 *   <add-feature>
 *     <form action="/extractions/:id/entity/new" method="post">
 *       <input type="hidden" name="type" value="tree"/>
 *       <button>Add tree</button>
 *     </form>
 *   </add-feature>
 */
export class AddFeature extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #focusTimeout

  connectedCallback () {
    const form = this.querySelector('form')
    if (!form) {
      console.warn('add-feature: No form found inside component')
      return
    }

    form.addEventListener('submit', (evt) => {
      evt.preventDefault()

      const type = new FormData(form).get('type')
      if (!type) {
        console.error('add-feature: Form has no type')
        return
      }

      const isTree = type === 'tree'
      const clientId = crypto.randomUUID()

      reactiveStore.editMode.set('add')
      reactiveStore.features.set([...reactiveStore.features.get(), {
        type: 'Feature',
        geometry: isTree
          ? { type: 'Point', coordinates: [] }
          : { type: 'Polygon', coordinates: [] },
        properties: {
          _id: clientId,
          feature_type: isTree ? 'tree' : 'tree_preservation_zone',
          name: isTree ? 'New tree' : 'New zone'
        }
      }])
      reactiveStore.selectedFeatureId.set(clientId)

      clearTimeout(this.#focusTimeout)
      this.#focusTimeout = window.setTimeout(() => {
        const el = /** @type {HTMLDetailsElement | null} */ (
          document.querySelector(`details[data-entity-id="${clientId}"]`)
        ) || /** @type {HTMLDetailsElement | null} */ (
          document.querySelector(`details[data-entity-id="${reactiveStore.selectedFeatureId.get()}"]`)
        )
        el?.focus()
      }, 100)
    })
  }
}

customElements.define('add-feature', AddFeature)
